import React, {useCallback, useMemo, useState} from 'react';
import {DayCellStyle, Grid, HeaderContainer, HeaderText, RowWrapper, WeekCellStyle} from './TimesheetsCalendar_styled';
import moment from 'moment';
import {TIME_PERIOD} from '../timesheets_change_date_view';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {Alert, FlexColumn, FlexRow, Link} from '@forecast-it/design-system';
import {Bounds, ContextMenuItem, Holiday, Person, TimeRegistrations, Week, Weeks} from './TimesheetsCalendarTypes';
import {
	getFirstDayOfWeek,
	getHolidaysOfTheDay,
	getStartDate,
	getWeekdaysShort,
	getWeeksOfMonth,
	LOCALSTORAGE_DISMISS_KEY,
	revokeTokens,
	WEEK_PREFIX,
	DAY_PREFIX,
} from './TimesheetsCalendarUtil';
import {CalendarEventType} from './calendar-events/CalendarEventsFetch';
import Moment from 'moment/moment';
import DayCell from './cells/DayCell';
import WeekCell from './cells/WeekCell';
import {openTimeRegModal} from './calendar-events/CalendarEventModalLogic';
import EventContextMenu from './calendar-events/EventContextMenu';
import CompanySetupUtil from '../../../shared/util/CompanySetupUtil';
import {isEventConvertedType, useConvertedEventTracking} from './calendar-events/useConvertedEventTracking';
import {getWeek, isSameDay} from 'date-fns';
import {AlertType} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/alert/types';
import GoogleCalendarOAuth from '../../../../containers/settings/settingsCalendar/GoogleCalendarOAuth';
import {TRACKING_OBJECTS} from '../../../../tracking/amplitude/constants/TrackingObjects';
import OutlookCalendarOAuth from '../../../../containers/settings/settingsCalendar/OutlookCalendarOAuth';
import {dispatch, EVENT_ID} from '@/containers/event_manager';

type TimesheetsCalendarProps = {
	timeRegistrations: TimeRegistrations;
	currentViewingDate: Date;
	currentViewingMonth: Date;
	currentPersonViewed: Person;
	selectDay: (day: moment.Moment) => void;
	selectWeek: (day: moment.Moment) => void;
	timePeriod: string;
	lockedDate: Date;
	calendarEvents: CalendarEventType[];
	isLoadingCalendarEvents: boolean;
	googleCalendarTokenError: string;
	googleCalendarFetchEnabled: boolean;
	outlookCalendarTokenError: string;
	outlookCalendarFetchEnabled: boolean;
	showNewSideModal: boolean;
	setClickedCalendarEventId: (clickedCalendarEventId: string | null) => void;
	isEventConvertedBySideModal: isEventConvertedType;
};

const TimesheetsCalendar = ({
	timeRegistrations,
	currentViewingDate,
	currentViewingMonth,
	currentPersonViewed,
	selectDay,
	selectWeek,
	timePeriod,
	calendarEvents,
	isLoadingCalendarEvents,
	lockedDate,
	googleCalendarTokenError,
	googleCalendarFetchEnabled,
	outlookCalendarTokenError,
	outlookCalendarFetchEnabled,
	showNewSideModal,
	setClickedCalendarEventId,
	isEventConvertedBySideModal,
}: TimesheetsCalendarProps): React.ReactElement => {
	const hideWeekend = CompanySetupUtil.weekendsAlwaysHidden();
	const [contextMenuItem, setContextMenuItem] = useState<ContextMenuItem | null>(null);
	const [bounds, setBounds] = useState<Bounds>({x: null, y: null});
	const {markEventAsConverted, isEventConverted: isEventConvertedByModal} = useConvertedEventTracking();
	const showContextMenu = useCallback(
		(e: MouseEvent, calendarEvent: CalendarEventType) => {
			setContextMenuItem({id: calendarEvent?.id, calendarEvent});
			setBounds({x: e.pageX, y: e.pageY});
		},
		[calendarEvents?.length]
	);

	const isEventConverted: isEventConvertedType = useCallback(
		(id?: string) => {
			return isEventConvertedByModal(id) || isEventConvertedBySideModal(id);
		},
		[isEventConvertedByModal, isEventConvertedBySideModal]
	);

	const [calendarInfoDismissCount, setCalendarInfoDismissCount] = useState<number>(
		Number(localStorage.getItem(LOCALSTORAGE_DISMISS_KEY))
	);
	const [calendarInfoDismissed, setCalendarInfoDismissed] = useState<boolean>(calendarInfoDismissCount > 2);
	const [isCurrentSelectionCalendarEvent, setIsCurrentSelectionCalendarEvent] = useState(false);

	const showGoogleCalendarInfo =
		googleCalendarFetchEnabled != null &&
		!googleCalendarFetchEnabled &&
		!calendarInfoDismissed &&
		!googleCalendarTokenError;
	const showOutlookCalendarInfo =
		outlookCalendarFetchEnabled != null &&
		!outlookCalendarFetchEnabled &&
		!calendarInfoDismissed &&
		!outlookCalendarTokenError;

	const closeContextMenu = useCallback(() => {
		if (contextMenuItem) {
			setContextMenuItem(null);
			setBounds({x: null, y: null});
		}
	}, [contextMenuItem, calendarEvents?.length]);

	const selectedDate = useMemo(
		() =>
			timePeriod === TIME_PERIOD.WEEK_VIEW
				? WEEK_PREFIX + getWeek(currentViewingDate)
				: DAY_PREFIX + currentViewingDate.getDate() + '-' + currentViewingDate.getMonth(),
		[currentViewingDate, timePeriod]
	);
	const firstDayOfWeek = useMemo(() => getFirstDayOfWeek(), []);

	const onSelectWeekCell = (day: Date): void => {
		trackEvent('Timesheet Calendar', 'Clicked', {selected: WEEK_PREFIX + getWeek(day)});
		selectWeek(Moment(day));
		setIsCurrentSelectionCalendarEvent(false);
	};

	const onSelectDayCell = (day: Date): void => {
		trackEvent('Timesheet Calendar', 'Clicked', {selected: `${DAY_PREFIX}${day.getDate()}/${day.getMonth() + 1}`});
		if (showNewSideModal) {
			dispatch(EVENT_ID.TIMESHEET_SHOW_SIDE_MODAL, {
				date: day,
				note: isCurrentSelectionCalendarEvent ? '' : null,
				minutes: isCurrentSelectionCalendarEvent ? 0 : null,
				clearSearchEntity: isCurrentSelectionCalendarEvent,
			});
		}
		selectDay(Moment(day));
		setIsCurrentSelectionCalendarEvent(false);
	};

	const weekArray: Weeks = getWeeksOfMonth(currentViewingMonth, hideWeekend);

	const getWeekDayHeaders = () => {
		const weekDaysShort = getWeekdaysShort(hideWeekend);
		return weekDaysShort.map(weekDay => {
			return (
				<DayCellStyle key={`${weekDay}-key`} hoverable={false}>
					<HeaderContainer>
						<FlexRow justifyContent={'center'}>
							<HeaderText>{weekDay}</HeaderText>
						</FlexRow>
					</HeaderContainer>
				</DayCellStyle>
			);
		});
	};

	const openModal = (calendarEvent: CalendarEventType) => {
		calendarEvent && setIsCurrentSelectionCalendarEvent(true);
		calendarEvent && setClickedCalendarEventId(calendarEvent.id);
		openTimeRegModal(currentPersonViewed, calendarEvent, markEventAsConverted, showNewSideModal, true);
	};

	const dismissCalendarInfo = () => {
		trackEvent(TRACKING_OBJECTS.LINK_GOOGLE_CALENDAR_ACCOUNT, 'Dismissed');
		const newDismissCount = calendarInfoDismissCount + 1;
		setCalendarInfoDismissCount(newDismissCount);
		setCalendarInfoDismissed(true);
		localStorage.setItem(LOCALSTORAGE_DISMISS_KEY, newDismissCount.toString());
	};

	const getWeekRow = (week: Week, index: number) => {
		const firstWeekOfMonth: boolean = index === 0;
		const holidays: Holiday[][] = week.days.map(day => getHolidaysOfTheDay(currentPersonViewed, day));
		const weekRow: JSX.Element[] = week.days.map((day: Date, index) => {
			const calendarEventsForDay = calendarEvents?.filter(event => {
				const eventDate = getStartDate(event);
				return eventDate ? isSameDay(eventDate, day) : false;
			});

			const isDayOutsideViewingMonth = day.getMonth() !== currentViewingMonth.getMonth();

			return (
				<DayCell
					timeRegistrations={timeRegistrations}
					currentPersonViewed={currentPersonViewed}
					selectedDate={selectedDate}
					onSelect={() => onSelectDayCell(day)}
					lockedDate={lockedDate}
					day={day}
					isDayOutsideViewingMonth={isDayOutsideViewingMonth}
					holidaysOfTheDay={holidays[index]}
					calendarEvents={calendarEventsForDay}
					isEventConverted={isEventConverted}
					isLoadingCalendarEvents={isLoadingCalendarEvents}
					currentViewingDate={currentViewingDate}
					openModal={openModal}
					showContextMenu={showContextMenu}
				></DayCell>
			);
		});
		const weekCell = (
			<WeekCell
				week={week}
				lockedDate={lockedDate}
				timeRegistrations={timeRegistrations}
				currentPersonViewed={currentPersonViewed}
				selectedDate={selectedDate}
				onSelect={() => onSelectWeekCell(firstWeekOfMonth ? week.days[6] : week.days[0])}
				hideWeekend={hideWeekend}
			></WeekCell>
		);
		weekRow.unshift(weekCell);
		return <RowWrapper key={`row-${index}-key`}>{weekRow}</RowWrapper>;
	};

	const gridRows: JSX.Element[] = weekArray.map((week, index) => {
		return getWeekRow(week, index);
	});

	return (
		<FlexColumn>
			{googleCalendarTokenError ? (
				<Alert
					text={'For security reasons you need to re-authenticate with your Google account'}
					type={AlertType.WARNING}
				>
					<Link onClick={() => revokeTokens(currentPersonViewed.id)}>Unlink account</Link>
					<GoogleCalendarOAuth calendarTokenError={googleCalendarTokenError} personId={currentPersonViewed.id} />
				</Alert>
			) : null}
			{showGoogleCalendarInfo ? (
				<Alert
					text={'Create time registrations from your Google Calendar'}
					type={AlertType.INFORMATION}
					onDismiss={() => dismissCalendarInfo()}
				>
					<GoogleCalendarOAuth personId={currentPersonViewed.id} />
				</Alert>
			) : null}
			{outlookCalendarTokenError ? (
				<Alert
					text={'For security reasons you need to re-authenticate with your Outlook account'}
					type={AlertType.WARNING}
				>
					<OutlookCalendarOAuth calendarTokenError={outlookCalendarTokenError} page="timesheets" />
				</Alert>
			) : null}
			{showOutlookCalendarInfo ? (
				<Alert
					text={'Create time registrations from your Outlook Calendar'}
					type={AlertType.INFORMATION}
					onDismiss={() => dismissCalendarInfo()}
				>
					<OutlookCalendarOAuth page="timesheets" />
				</Alert>
			) : null}
			<Grid firstDayOfWeek={firstDayOfWeek} hideWeekend={hideWeekend}>
				<RowWrapper>
					<WeekCellStyle hoverable={false}>
						<HeaderContainer>
							<FlexRow justifyContent={'center'}>
								<HeaderText>Week total</HeaderText>
							</FlexRow>
						</HeaderContainer>
					</WeekCellStyle>
					{getWeekDayHeaders()}
				</RowWrapper>
				{gridRows}
			</Grid>
			{contextMenuItem ? (
				<EventContextMenu
					closeContextMenu={closeContextMenu}
					contextMenuItem={contextMenuItem}
					bounds={bounds}
					person={currentPersonViewed}
					markEventAsConverted={markEventAsConverted}
					sideModalOpen={showNewSideModal}
				/>
			) : null}
		</FlexColumn>
	);
};

export default TimesheetsCalendar;
